/**
 * The one door to Astra, in one file.
 *
 * The event NAME lived in Navigation.jsx and was retyped as a literal in two
 * other components (AstraPrompts, YourProfile) to avoid dragging the whole
 * nav module into their chunks. Two copies of a string is how the
 * campus-events whitelist rotted four times, so the name moved here: a leaf
 * module with no imports, free for anyone to pull in. Navigation still
 * re-exports it, because a dozen call sites and tests import it from there.
 *
 * The /astra page (#2210) is now where a door LANDS, so the encoding of the
 * request — the question, the school it is about, the conversation to
 * resume — lives here too. The launcher writes it, the page reads it, and
 * neither one owns the format alone.
 */
export const OPEN_ASTRA_EVENT = 'stratia:open-astra';

export const ASTRA_PATH = '/astra';
// Astra Today (#3754, ADR 0041): where a pilot student's sign-in lands.
export const TODAY_PATH = '/today';

/**
 * The topics a door can file a question under (#2233). Same allow-list as
 * THREAD_TOPICS in cloud_functions/counselor_agent/concierge_chat.py, which
 * pins the two with a drift test; an unknown topic is dropped, never guessed.
 */
export const ASTRA_TOPICS = ['activities', 'programs', 'majors', 'roadmap', 'essays',
    'research', 'strategy', 'launchpad', 'profile', 'agents', 'builder', 'pitch'];

const topicOf = (t) => (typeof t === 'string' && ASTRA_TOPICS.includes(t.trim().toLowerCase())
    ? t.trim().toLowerCase() : null);

/**
 * {question?, scope?, threadId?} -> "/astra?ask=...&scope=...&thread=..."
 * Anything absent is simply left out, so a plain open is a plain "/astra".
 */
// A move id as the server writes it (astra_moves.move_id): anything else is
// dropped, never guessed.
export const MOVE_ID = /^[a-z0-9_-]{1,140}$/;

export function astraHref({ question = null, scope = null, threadId = null, topic = null, move = null } = {}) {
    const params = new URLSearchParams();
    // Astra Today (#3786): the badge's door opens on the move it promised.
    if (typeof move === 'string' && MOVE_ID.test(move)) params.set('move', move);
    const ask = typeof question === 'string' ? question.trim() : '';
    if (ask) params.set('ask', ask);
    const t = topicOf(topic);
    if (t) params.set('topic', t);
    if (scope?.university_id) {
        params.set('scope', JSON.stringify({
            university_id: String(scope.university_id),
            university_name: scope.university_name ? String(scope.university_name) : null,
        }));
    }
    if (threadId) params.set('thread', String(threadId));
    const qs = params.toString();
    return qs ? `${ASTRA_PATH}?${qs}` : ASTRA_PATH;
}

/**
 * The inverse. A malformed `scope` is dropped rather than guessed at — a
 * conversation filed under a school we cannot name is worse than a general
 * one, and the question itself must still survive.
 */
export function parseAstraQuery(search) {
    const params = new URLSearchParams(search || '');
    const ask = params.get('ask');
    const threadId = params.get('thread');
    let scope = null;
    const raw = params.get('scope');
    if (raw) {
        try {
            const parsed = JSON.parse(raw);
            if (parsed?.university_id) {
                scope = {
                    university_id: String(parsed.university_id),
                    university_name: parsed.university_name ? String(parsed.university_name) : null,
                };
            }
        } catch { /* an unreadable scope is no scope */ }
    }
    return {
        ask: ask && ask.trim() ? ask : null,
        scope,
        threadId: threadId || null,
        topic: topicOf(params.get('topic')),
        move: MOVE_ID.test(params.get('move') || '') ? params.get('move') : null,
    };
}
